import { Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { SelectionModel } from '@angular/cdk/collections';
import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { ISnFeedQueryParameters, ISnListQueryParameters, ISnListResponse } from '@lib/api';
import { SnDataListComponent } from '@lib/ui/data-list';
import { SnLocation } from 'libs/api/entities/src/lib/location';
import { SnLocationsAPI } from 'libs/api/services/src/lib/locations.service';

@Component({
  selector: 'sn-location-search-dialog',
  templateUrl: './location-search-dialog.component.html',
  styleUrls: ['./location-search-dialog.component.scss'],
})
export class LocationSearchDialogComponent implements OnInit, OnDestroy {
  @ViewChild('list', { static: false })
  public readonly list: SnDataListComponent<SnLocation>;
  public readonly loadData = this._loadData.bind(this);
  public readonly feedData = this._feedData.bind(this);
  public selection = new SelectionModel<SnLocation>(true, []);
  public readonly columns: string[] = ['select', 'name', 'location', 'floor', 'owner'];
  public _querySearch: string;
  public pageSize;
  public selected;

  private readonly _destroy$ = new Subject();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly _dialogRef: MatDialogRef<LocationSearchDialogComponent>,
    private readonly _locationsAPI: SnLocationsAPI,
  ) {}

  public ngOnInit(): void {
    this.selected = '20';
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public onSearchValueChange(value: string): void {
    this._querySearch = value;
    this._reloadData();
  }

  public onSelected(location: any): void {
    this._dialogRef.close(location);
  }

  public cancel(): void {
    this._dialogRef.close();
  }

  public isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.list.dataSource.data.length;
    return numSelected === numRows;
  }

  public loadAllLocations(): void {
    this.pageSize = 'all';
    this._reloadData();
  }

  public loadTenLocations(): void {
    this.pageSize = 10;
    this._reloadData();
  }

  public loadTwentyLocations(): void {
    this.pageSize = 20;
    this._reloadData();
  }

  public masterToggle(): void {
    this.isAllSelected()
      ? this.selection.clear()
      : this.list.dataSource.data.forEach((row) => this.selection.select(row));
  }

  public call(): void {
    const locations = this.selection.selected.map((location) => location);
    this.onSelected(locations);
  }

  private _reloadData(): void {
    this.list.reference.reload();
  }

  private _loadData(params?: ISnListQueryParameters): Observable<ISnListResponse<SnLocation>> {
    params.search = this._querySearch;
    return this._locationsAPI
      .getLocationList(this._prepareQueryParams(params))
      .pipe(takeUntil(this._destroy$));
  }

  private _feedData(params: ISnFeedQueryParameters): Observable<SnLocation[]> {
    return this._locationsAPI.feedLocations(this._prepareQueryParams(params));
  }

  private _prepareQueryParams<T extends ISnListQueryParameters>(params: any): any {
    return {
      ...params,
      ordering: {
        active: 'name',
        direction: 'asc',
      },
      page: params.page,
      pageSize: this.pageSize ? this.pageSize : params.pageSize,
    };
  }
}
